import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Typography,
} from "@mui/material";
import { useEditors, useEditorMutations } from "hooks/survey";
import { getEmployees } from "api/cansurvey/surveyApi";
import { useSelector } from "react-redux";
import { selectUserInfo } from "redux/userInfo/userInfoSlice";
import type { SurveyEmployee } from "types/survey";

interface ManageEditorsDialogProps {
  open: boolean;
  surveyId: number;
  onClose: () => void;
  onSaved: () => void;
}

const ManageEditorsDialog: React.FC<ManageEditorsDialogProps> = ({
  open,
  surveyId,
  onClose,
  onSaved,
}) => {
  const { userInfo } = useSelector(selectUserInfo);
  const currentEmployeeId = parseInt(userInfo?.employeeId || "0");
  const currentUserName = userInfo?.fullname || userInfo?.firstName || "You";

  const { editors, loading: editorsLoading, error: editorsError } = useEditors(open ? surveyId : null);
  const { setEditorsMutation, saving } = useEditorMutations();

  const [employees, setEmployees] = useState<SurveyEmployee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<SurveyEmployee[]>([]);
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const initializedRef = useRef(false);

  // Fetch employees when dialog opens
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setEmployeesLoading(true);
    getEmployees()
      .then((data) => {
        if (!cancelled) setEmployees(data);
      })
      .catch((err) => {
        console.error('[ManageEditorsDialog] Failed to fetch employees:', err);
        if (!cancelled) setEmployeesError(err instanceof Error ? err.message : 'Failed to load employees');
      })
      .finally(() => {
        if (!cancelled) setEmployeesLoading(false);
      });

    return () => { cancelled = true; };
  }, [open]);

  // Filter out the creator from the dropdown options (they're shown as a fixed chip)
  const availableEmployees = useMemo(
    () => employees.filter((e) => e.employeeId !== currentEmployeeId),
    [employees, currentEmployeeId]
  );

  // Initialize selected employees from current editors once both data sources are ready
  useEffect(() => {
    if (initializedRef.current || editorsLoading || employeesLoading || availableEmployees.length === 0) return;

    initializedRef.current = true;
    const selected = editors
      .map((editor) => availableEmployees.find((e) => e.employeeId === editor.employeeId))
      .filter((e): e is SurveyEmployee => e !== undefined);
    setSelectedEmployees(selected);
  }, [editors, availableEmployees, editorsLoading, employeesLoading]);

  // Reset initialization when dialog reopens
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setSelectedEmployees([]);
      setEmployeesError(null);
      setSaveError(null);
    }
  }, [open]);

  const handleSave = async () => {
    setSaveError(null);
    try {
      await setEditorsMutation(
        surveyId,
        selectedEmployees.map((e) => e.employeeId)
      );
      onSaved();
      onClose();
    } catch (err) {
      console.error('[ManageEditorsDialog] Failed to save editors:', err);
      setSaveError(err instanceof Error ? err.message : "Failed to save editors");
    }
  };

  const isLoading = editorsLoading || employeesLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Editors</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Editors can edit this survey and view its results. Only you (the creator) can delete it or manage editors.
        </Typography>
        {isLoading ? (
          <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} />
        ) : editorsError || employeesError ? (
          <Alert severity="error" sx={{ mb: 2 }}>{editorsError || employeesError}</Alert>
        ) : (
          <>
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>
            )}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5, mt: 1 }}>
              <Chip
                label={`${currentUserName} (Creator)`}
                size="small"
                sx={{ opacity: 0.6 }}
              />
              {selectedEmployees.map((employee) => (
                <Chip
                  key={employee.employeeId}
                  label={employee.name}
                  size="small"
                  onDelete={() => setSelectedEmployees((prev) => prev.filter((e) => e.employeeId !== employee.employeeId))}
                />
              ))}
            </Box>
            <Autocomplete
              multiple
              size="small"
              options={availableEmployees}
              getOptionLabel={(option) => option.name}
              value={selectedEmployees}
              onChange={(_, newValue) => setSelectedEmployees(newValue)}
              isOptionEqualToValue={(option, value) => option.employeeId === value.employeeId}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                  {option.name}
                </li>
              )}
              renderTags={() => null}
              renderInput={(params) => (
                <TextField {...params} label="Search employees" placeholder="Add editors..." />
              )}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || saving}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageEditorsDialog;
